import { useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Clock, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  loadRecentRepos,
  saveRecentRepo,
  subscribeRecentReposChange,
} from "@/lib/recent-repos";
// Auth gating is handled upstream by <RequireAuth> in the router (App.tsx).

const formSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(3, "Too short")
    .regex(
      /^[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9._-]+$/,
      "Expected owner/repo (letters, numbers, ._-)",
    ),
});

type FormValues = z.infer<typeof formSchema>;

export function RepoPicker() {
  const navigate = useNavigate();
  const recent = useSyncExternalStore(
    subscribeRecentReposChange,
    loadRecentRepos,
    loadRecentRepos,
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { slug: "" },
  });

  function onSubmit(values: FormValues) {
    const [owner, name] = values.slug.split("/");
    if (!owner || !name) return;
    // saveRecentRepo notifies subscribers → useSyncExternalStore re-renders.
    saveRecentRepo(`${owner}/${name}`);
    navigate(`/repo/${owner}/${name}`);
  }

  function pickRecent(slug: string) {
    const [owner, name] = slug.split("/");
    if (!owner || !name) return;
    navigate(`/repo/${owner}/${name}`);
  }

  return (
    <div className="container mx-auto px-4 py-16 max-w-xl">
      <Card>
        <CardHeader>
          <CardTitle>Pick a repository</CardTitle>
          <CardDescription>
            Enter the full slug in the form <code className="font-mono">owner/repo</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Repository</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                        <Input
                          autoFocus
                          placeholder="vercel/next.js"
                          className="pl-9"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit">
                  Analyze
                  <ArrowRight className="size-4 ml-2" />
                </Button>
              </div>
            </form>
          </Form>

          {recent.length > 0 ? (
            <div className="mt-8">
              <h3 className="text-sm font-medium flex items-center gap-2 mb-2">
                <Clock className="size-4" />
                Recent
              </h3>
              <ul className="flex flex-wrap gap-2">
                {recent.map((slug) => (
                  <li key={slug}>
                    <Button variant="outline" size="sm" onClick={() => pickRecent(slug)}>
                      {slug}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
